import type {
  Feed as KnockFeed,
  FeedItem as KnockFeedItem,
  KnockGuideStep,
} from '@knocklabs/client';
import type {
  IpnEventData as SegmentIpnEventData,
  IpnEventName,
} from '@packages/shared/segment-typewriter';

import {
  IPN_IMPLEMENTATION,
  type IpnImplementation,
  type IpnCategory,
  type IpnType,
  type IpnSurface,
  type IpnSlot,
  type IpnAction,
  type IpnCtaElement,
  type IpnCtaType,
  type IpnFeature,
} from './ipnTelemetryTypes';

import {initTelemetry, type Telemetry} from './telemetry';

export interface IpnEventDataWithRestrictedProperties {
  implementation: IpnImplementation;
  category: IpnCategory;
  ipn_type: IpnType;
  surface: IpnSurface;
  slot: IpnSlot;
  action: IpnAction;
  cta_type?: IpnCtaType;
  cta_element?: IpnCtaElement;
  feature?: IpnFeature;
}

export type IpnEventData<
  K extends IpnEventName,
  T extends IpnImplementation,
> = SegmentIpnEventData<K> &
  {
    IPN_COLLISION: IpnEventDataWithRestrictedProperties;
    IPN_DISMISSED: IpnEventDataWithRestrictedProperties;
    IPN_INTERACTED: IpnEventDataWithRestrictedProperties;
    IPN_VIEWED: IpnEventDataWithRestrictedProperties;
  }[K] &
  {
    [IPN_IMPLEMENTATION.KNOCK_GUIDE]: {
      step: KnockGuideStep;
    };
    [IPN_IMPLEMENTATION.KNOCK_WORKFLOW]: {
      feed: KnockFeed;
      feedItem: KnockFeedItem;
    };
    [IPN_IMPLEMENTATION.ITERABLE]: {
      messageId: string;
    };
    [IPN_IMPLEMENTATION.BESPOKE]: {
      implementation: typeof IPN_IMPLEMENTATION.BESPOKE;
    };
    [IPN_IMPLEMENTATION.CALLOUT_POPOVER]: {
      implementation: typeof IPN_IMPLEMENTATION.CALLOUT_POPOVER;
    };
    [IPN_IMPLEMENTATION.NOTIFICATION_STORE]: {
      implementation: typeof IPN_IMPLEMENTATION.NOTIFICATION_STORE;
    };
  }[T];

export const isValidKnockGuideIpnEventData = (
  data: any
): data is IpnEventData<IpnEventName, 'knock_guide'> =>
  data && data.implementation === IPN_IMPLEMENTATION.KNOCK_GUIDE && data.step;

export const isValidKnockWorkflowIpnEventData = (
  data: any
): data is IpnEventData<IpnEventName, 'knock_workflow'> =>
  data &&
  data.implementation === IPN_IMPLEMENTATION.KNOCK_WORKFLOW &&
  data.feed &&
  data.feedItem;

export const isValidIterableIpnEventData = (
  data: any
): data is IpnEventData<IpnEventName, 'iterable'> =>
  data && data.implementation === IPN_IMPLEMENTATION.ITERABLE && data.messageId;

export type IpnTelemetrySendEvent = <
  K extends IpnEventName,
  T extends IpnImplementation,
>(
  eventName: K,
  data: IpnEventData<K, T>
) => void;

export type IpnTelemetry<Context> = Telemetry<Context> & {
  sendIpnEvent: IpnTelemetrySendEvent;
};

export function initIpnTelemetry(): IpnTelemetry<any> {
  const telemetry = initTelemetry();
  return {
    ...telemetry,
    sendIpnEvent: telemetry.sendEvent,
  };
}
